import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import * as z from 'zod';

import { ILogger } from '@shure/shared/angular/utils/logging';

const GO_TO_STEP_STORAGE_KEY = 'shure.claiming.goToQrClaimingStep';
const CLAIM_DEVICES_STORAGE_KEY = 'shure.claiming.claimDevices';
const CLAIM_LINK_SELECTION_STEP_STORAGE_KEY = 'shure.claiming.goToClaimLinkSelectionStep';

const claimDeviceSchema = z.object({
	mac: z.string(),
	serial: z.string()
});

const claimDevicesSchema = z.array(claimDeviceSchema);

export type ClaimDevice = z.infer<typeof claimDeviceSchema>;

export enum SaveClaimDeviceResult {
	Success,
	Duplicate
}

@Injectable({ providedIn: 'root' })
export class ClaimingFlowService {
	private readonly logger: ILogger;
	private readonly devices$ = new BehaviorSubject<ClaimDevice[]>(this.getDevices());

	constructor(logger: ILogger) {
		this.logger = logger.createScopedLogger('ClaimingFlowService');
	}

	/**
	 * Sets a flag in session storage to indicate that the user should be immediately taken to the
	 * QR code claiming step.
	 */
	public setGoToQrClaimingStepFlag(): void {
		sessionStorage.setItem(GO_TO_STEP_STORAGE_KEY, 'true');
	}

	/**
	 * Reads the go to QR claiming step flag from local storage and returns a boolean indicating whether
	 * or not the flag was set. Discards the flag after reading it.
	 */
	public readGoToQrClaimingStepFlag(): boolean {
		const shouldGoToStep = sessionStorage.getItem(GO_TO_STEP_STORAGE_KEY) !== null;
		sessionStorage.removeItem(GO_TO_STEP_STORAGE_KEY);
		return shouldGoToStep;
	}

	/**
	 * Return an observable that emits the full array of claim devices every time a new value is stored.
	 */
	public getDevices$(): Observable<ClaimDevice[]> {
		return this.devices$.asObservable();
	}

	/**
	 * Gets the current value of the stored claim devices.
	 */
	public getDevices(): ClaimDevice[] {
		try {
			const devicesJson = sessionStorage.getItem(CLAIM_DEVICES_STORAGE_KEY);
			return devicesJson ? claimDevicesSchema.parse(JSON.parse(devicesJson)) : [];
		} catch (e) {
			// If the stored devices are in an erroneous format, log an error message and clear the current value.
			this.logger.error('getDevices', 'Unable to parse stored devices. Clearing cache.', e);
			sessionStorage.removeItem(CLAIM_DEVICES_STORAGE_KEY);
			return [];
		}
	}

	/**
	 * Saves a claim device to storage. Returns Duplicate if the same MAC/serial is already stored.
	 */
	public saveDevice(code: ClaimDevice): SaveClaimDeviceResult {
		const devices = this.getDevices();

		if (devices.some((saved) => saved.mac === code.mac && saved.serial === code.serial)) {
			return SaveClaimDeviceResult.Duplicate;
		}

		devices.push(code);
		sessionStorage.setItem(CLAIM_DEVICES_STORAGE_KEY, JSON.stringify(devices));
		this.devices$.next(devices);
		return SaveClaimDeviceResult.Success;
	}

	/**
	 * Replaces the stored claim devices with the provided list.
	 */
	public setDevices(devices: ClaimDevice[]): void {
		sessionStorage.setItem(CLAIM_DEVICES_STORAGE_KEY, JSON.stringify(devices));
		this.devices$.next(devices);
	}

	/**
	 * Clears the QR-claiming flag, stored claim devices, and claim-link selection-step flag.
	 */
	public clear(): void {
		sessionStorage.removeItem(GO_TO_STEP_STORAGE_KEY);
		sessionStorage.removeItem(CLAIM_DEVICES_STORAGE_KEY);
		sessionStorage.removeItem(CLAIM_LINK_SELECTION_STEP_STORAGE_KEY);
		this.devices$.next([]);
	}

	/**
	 * Sets a flag in session storage to indicate that a claim-link flow should go directly to the
	 * Step 2 selection view, skipping QR scanning.
	 */
	public setGoToClaimLinkSelectionStepFlag(): void {
		sessionStorage.setItem(CLAIM_LINK_SELECTION_STEP_STORAGE_KEY, 'true');
	}

	/**
	 * Reads whether a claim-link flow should skip directly to the Step 2 selection view.
	 * Discards the flag after reading it.
	 */
	public readGoToClaimLinkSelectionStepFlag(): boolean {
		const shouldGoToSelectionStep = sessionStorage.getItem(CLAIM_LINK_SELECTION_STEP_STORAGE_KEY) !== null;
		sessionStorage.removeItem(CLAIM_LINK_SELECTION_STEP_STORAGE_KEY);
		return shouldGoToSelectionStep;
	}
}
